/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  sortingFns,
  useReactTable,
} from '@tanstack/react-table';
import {
  rankItem,
  compareItems,
} from '@tanstack/match-sorter-utils';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { LuChevronLeft, LuChevronRight, LuChevronsLeft, LuChevronsRight } from 'react-icons/lu';

export const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank });
  return itemRank.passed;
};

export const fuzzySort = (rowA, rowB, columnId) => {
  let dir = 0;
  if (rowA?.columnFiltersMeta[columnId]) {
    dir = compareItems(
      rowA?.columnFiltersMeta[columnId]?.itemRank,
      rowB?.columnFiltersMeta[columnId]?.itemRank
    );
  }
  return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};

export default function CustomTable({ data, columns, tableTop, isApiPagination=false, pageSize, showSerialNumber=true }) {
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [globalFilter, setGlobalFilter] = React.useState('');

  const table = useReactTable({
    data,
    columns,
    filterFns: { fuzzy: fuzzyFilter },
    state: { columnFilters, globalFilter },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'fuzzy',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: false,
  });

  React.useEffect(() => {
    if (table?.getState()?.columnFilters[0]?.id === 'fullName') {
      if (table?.getState().sorting[0]?.id !== 'fullName') {
        table.setSorting([{ id: 'fullName', desc: false }]);
      }
    };
  }, [table?.getState()?.columnFilters[0]?.id]);
  React.useEffect(() => {
    if(pageSize){
      table.setPageSize(pageSize);
    } // Set page size based on prop
  }, [pageSize]);
  return (
    <>
      {data && columns ? (
        <div className="p-2">
          <div className='flex justify-between items-center'>
            <div>
              <DebouncedInput
                value={globalFilter ?? ''}
                onChange={value => setGlobalFilter(String(value))}
                className="p-2 font-lg shadow border border-block mb-2 rounded-sm"
                placeholder="Search all columns..."
              />
            </div>
            <div>
              {tableTop}
            </div>
          </div>

          <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
            <table className="w-full text-sm text-left rtl:text-right text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {showSerialNumber && <th scope="col" className="px-6 py-3">#</th>}
                    {headerGroup.headers.map(header => (
                      <th scope="col" className="px-6 py-3" key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder ? null : (
                          <div
                            {...{
                              className: header.column?.getCanSort() ? 'cursor-pointer select-none flex gap-2 items-center mb-1' : '',
                              onClick: header.column?.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(header.column?.columnDef.header, header.getContext())}
                            {{
                              asc: <FaChevronUp />,
                              desc: <FaChevronDown />,
                            }[header.column?.getIsSorted()] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows?.map((row, index) => (
                  <tr className="odd:bg-white even:bg-gray-50 border-b" key={row.id}>
                    {showSerialNumber && <td className="px-6 py-4">{index + 1}</td>}
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-6 py-4">
                        {flexRender(cell.column?.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="h-2" />
         {!isApiPagination && <div className="flex items-center justify-between flex-col gap-2 mt-3 md:flex-row">
            <div className='flex items-center'>
              <button
                className="border rounded p-2"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <LuChevronsLeft />
              </button>
              <button
                className="border rounded p-2"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <LuChevronLeft />
              </button>
              <button
                className="border rounded p-2"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <LuChevronRight />
              </button>
              <button
                className="border rounded p-2"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <LuChevronsRight />
              </button>
            </div>
            <span className="flex items-center gap-1">
              <div>Page</div>
              <strong>
                {table?.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </strong>
            </span>
            <div className='flex items-center gap-2'>
              <span className="flex items-center gap-1">
                Go to page:
                <input
                  type="number"
                  className="'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-20 p-2.5"
                  defaultValue={table?.getState().pagination.pageIndex + 1}
                  onChange={e => {
                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                    table.setPageIndex(page);
                  }}
                />
              </span>
              <select
                value={table?.getState().pagination.pageSize}
                onChange={e => {
                  table.setPageSize(Number(e.target.value));
                }}
              >
                {[10, 20, 30, 40, 50].map(pageSize => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
                ))}
              </select>
            </div>
          </div>}
        </div>
      ) : (
        <div>Loading...</div>
      )}
    </>
  );
}

function DebouncedInput({ value: initialValue, onChange, debounce = 500, ...props }) {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <input
      className='bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5'
      {...props}
      value={value}
      onChange={e => setValue(e.target.value)}
    />
  );
}
